import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CircleDollarSign, CreditCard, Edit, Eye, KeyRound, Loader2, MoreHorizontal, Plus, Search, Trash2, Users } from 'lucide-react'
import { toast } from 'sonner'

import ResetPasswordDialog from '@/components/users/reset-password-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { CompactTableDate, commerceTableClassName } from '@/components/common/compact-table-date'
import { orgUsersApi } from '@/services/org-users'
import type { OrgUser, OrgUserAddress } from '@/services/org-users'
import { orgUserAdminApi } from '@/services/org-user-admin'
import { getErrorMessage } from '@/lib/api'
import { normalizeOrgUserGroupKey } from '@/lib/org-user-groups'
import { toTimeInputValue, USER_ADDRESS_TYPE } from '@/lib/user-address'
import { buildJobsListPath } from '@/lib/jobs-list-navigation'
import { withSystemRoleCapabilityFallback } from '@/lib/route-access'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { formatCurrency } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import { Badge } from '@/components/ui/badge'
import { useTenantTimeZone } from '@/lib/tenant-date-time'

type CustomerDirectoryContext = 'bookings' | 'jobs'
type CustomerTabKey = 'retail' | 'business'

const normalizeCustomerTab = (value: string | null | undefined): CustomerTabKey => {
  return String(value ?? '').trim().toLowerCase() === 'business' ? 'business' : 'retail'
}

const getCustomerDisplayName = (user: {
  title?: string | null
  fname?: string | null
  mname?: string | null
  lname?: string | null
  username?: string | null
  usr_reg_num?: string | null
}) => {
  const name = [
    user.title,
    user.fname,
    user.mname,
    user.lname,
  ]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim()

  const baseName = name || String(user.username ?? '').trim() || 'Unknown customer'
  const regNum = String(user.usr_reg_num ?? '').trim()
  if (!regNum || regNum === '0') {
    return baseName
  }

  return `${baseName} (${regNum})`
}

const getStatusBadgeClassName = (status?: string | null) => {
  const normalized = String(status ?? '').trim().toLowerCase()
  if (normalized === 'inactive' || normalized === '0') {
    return 'border-rose-200 bg-rose-50 text-rose-700'
  }
  if (normalized === 'active' || normalized === '1') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  }
  return 'border-slate-200 bg-slate-50 text-slate-700'
}

const formatUnpaidAmount = (value?: string | number | null) => {
  const amount = Number(value ?? 0)
  if (!Number.isFinite(amount) || amount <= 0) {
    return '-'
  }
  return formatCurrency(amount)
}

type CustomersDirectoryPageProps = {
  context: CustomerDirectoryContext
}

type CustomerEditFormState = {
  title: string
  fname: string
  lname: string
  email: string
  phone: string
  openingTime: string
  closingTime: string
  saturdayClosed: boolean
  sundayClosed: boolean
  primaryAddressId: string | number | null
}

const getPrimaryBusinessAddress = (addresses: OrgUserAddress[]) => {
  return addresses.find((address) => address.default)
    ?? addresses.find((address) => address.type === USER_ADDRESS_TYPE.BUSINESS)
    ?? addresses[0]
    ?? null
}

const createEmptyCustomerFormState = (): CustomerEditFormState => ({
  title: '',
  fname: '',
  lname: '',
  email: '',
  phone: '',
  openingTime: '',
  closingTime: '',
  saturdayClosed: false,
  sundayClosed: false,
  primaryAddressId: null,
})

const createCustomerEditFormState = (
  customer: OrgUser,
  primaryAddress?: OrgUserAddress | null,
): CustomerEditFormState => ({
  title: customer.title || '',
  fname: customer.fname || '',
  lname: customer.lname || '',
  email: customer.email || '',
  phone: customer.phone || '',
  openingTime: toTimeInputValue(primaryAddress?.opening_time),
  closingTime: toTimeInputValue(primaryAddress?.closing_time),
  saturdayClosed: Boolean(primaryAddress?.saturday_closed),
  sundayClosed: Boolean(primaryAddress?.sunday_closed),
  primaryAddressId: primaryAddress?.id ?? null,
})

export default function CustomersDirectoryPage({ context }: CustomersDirectoryPageProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  // Customer dates in the tenant timezone once it is known; until then the existing output.
  const customerTimeZone = useTenantTimeZone(serviceInstanceId)
    || useAuthStore((state) => state.serviceInstanceId)
  const currentUserId = useAuthStore((state) => state.user?.id)
  const currentPost = useAuthStore((state) => state.currentPost)
  const currentCapabilities = useAuthStore((state) => state.currentPost?.capabilities)
  const rolePermissionCapabilities = useMemo(() => {
    const permissions = currentPost?.roles_data?.permissions
    if (!permissions || typeof permissions !== 'object') {
      return undefined
    }

    const normalized: Record<string, boolean> = {}
    for (const [key, value] of Object.entries(permissions as Record<string, unknown>)) {
      normalized[key] = value === true || value === 1 || String(value).toLowerCase() === 'true'
    }
    return normalized
  }, [currentPost?.roles_data?.permissions])
  const effectiveCapabilities = useMemo(() => {
    if (!currentCapabilities && !rolePermissionCapabilities) {
      return withSystemRoleCapabilityFallback(currentPost, undefined)
    }

    return withSystemRoleCapabilityFallback(currentPost, {
      ...(rolePermissionCapabilities ?? {}),
      ...(currentCapabilities ?? {}),
    })
  }, [currentCapabilities, currentPost, rolePermissionCapabilities])
  const canForcePasswordResetUsers = Boolean(effectiveCapabilities?.can_force_password_reset_users)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [activeTab, setActiveTab] = useState<CustomerTabKey>(() => normalizeCustomerTab(new URLSearchParams(location.search).get('tab')))
  const [editingCustomer, setEditingCustomer] = useState<OrgUser | null>(null)
  const [isAddCustomerDialogOpen, setIsAddCustomerDialogOpen] = useState(false)
  const [deletingCustomer, setDeletingCustomer] = useState<OrgUser | null>(null)
  const [resetPasswordCustomer, setResetPasswordCustomer] = useState<OrgUser | null>(null)
  const [editForm, setEditForm] = useState<CustomerEditFormState>(createEmptyCustomerFormState)

  useEffect(() => {
    setActiveTab(normalizeCustomerTab(new URLSearchParams(location.search).get('tab')))
  }, [location.search])

  const groupsQuery = useQuery({
    queryKey: ['customer-directory-groups', serviceInstanceId],
    queryFn: () => orgUsersApi.listGroups({ serviceInstanceId }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const activeGroupId = useMemo(() => {
    const targetKey = activeTab === 'business' ? 'business_customer' : 'retail_customer'
    const match = (groupsQuery.data ?? []).find((group) => {
      const codeKey = normalizeOrgUserGroupKey(group.unique_code)
      const titleKey = normalizeOrgUserGroupKey(group.title)
      return codeKey === targetKey || titleKey === targetKey
    })
    return match?.id ?? null
  }, [activeTab, groupsQuery.data])

  const customersQuery = useQuery({
    queryKey: ['customer-directory', context, activeTab, activeGroupId, search, page, pageSize, serviceInstanceId],
    queryFn: () => orgUsersApi.listPaginated({
      search,
      page,
      pageSize,
      userGroupId: activeGroupId ?? undefined,
      serviceInstanceId,
      includeCustomerActivity: true,
    }),
    enabled: Boolean(serviceInstanceId && activeGroupId),
    staleTime: 20_000,
  })

  const handleTabChange = (value: string) => {
    const nextTab = normalizeCustomerTab(value)
    setActiveTab(nextTab)
    setPage(1)

    const params = new URLSearchParams(location.search)
    if (nextTab === 'retail') {
      params.delete('tab')
    } else {
      params.set('tab', nextTab)
    }

    const nextSearch = params.toString()
    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true })
  }

  const buildBookingsLink = (userId: string | number, scope: 'open' | 'completed') => {
    const params = new URLSearchParams()
    params.set('customer_id', String(userId))
    params.set('booking_scope', scope)
    return withServiceInstanceId(`/services/orders?${params.toString()}`, serviceInstanceId)
  }

  const buildCreditLink = (userId: string | number) => {
    const params = new URLSearchParams()
    params.set('user_id', String(userId))
    return withServiceInstanceId(`/services/credits?${params.toString()}`, serviceInstanceId)
  }

  const buildJobsLink = (userId: string | number, scope: 'open' | 'completed') => {
    return withServiceInstanceId(
      buildJobsListPath({
        serviceInstanceId,
        clientUser: userId,
        jobScope: scope,
      }),
      serviceInstanceId,
    )
  }

  const buildCustomerDetailLink = (customerId: string | number) => {
    const params = new URLSearchParams(location.search)
    if (serviceInstanceId) {
      params.set('service_instance_id', String(serviceInstanceId))
    }
    if (activeGroupId) {
      params.set('user_group_id', String(activeGroupId))
    }
    if (activeTab === 'business') {
      params.set('tab', 'business')
    } else {
      params.delete('tab')
    }

    const basePath = context === 'bookings' ? '/services/customers' : '/job/customers'
    const searchParams = params.toString()
    return `${basePath}/${customerId}${searchParams ? `?${searchParams}` : ''}`
  }

  const openEditCustomerDialog = async (customer: OrgUser) => {
    setEditingCustomer(customer)

    if (activeTab === 'business') {
      const userId = customer.user_id ?? customer.id
      if (userId) {
        try {
          const addresses = await orgUsersApi.listAddresses(String(userId), { includeInactive: true })
          setEditForm(createCustomerEditFormState(customer, getPrimaryBusinessAddress(addresses)))
          return
        } catch {
          // Fall back to contact-only form if address lookup fails.
        }
      }
    }

    setEditForm(createCustomerEditFormState(customer))
  }

  const closeEditCustomerDialog = () => {
    setEditingCustomer(null)
    setEditForm(createEmptyCustomerFormState())
  }

  const openAddCustomerDialog = () => {
    setEditingCustomer(null)
    setEditForm(createEmptyCustomerFormState())
    setIsAddCustomerDialogOpen(true)
  }

  const closeAddCustomerDialog = () => {
    setIsAddCustomerDialogOpen(false)
    setEditForm(createEmptyCustomerFormState())
  }

  const updateCustomerMutation = useMutation({
    mutationFn: async () => {
      if (!editingCustomer) {
        throw new Error('No customer selected.')
      }
      const customerId = String(editingCustomer.id ?? editingCustomer.user_id)
      const updatedCustomer = await orgUsersApi.updateClient(customerId, {
        title: editForm.title.trim() || undefined,
        fname: editForm.fname.trim(),
        lname: editForm.lname.trim() || undefined,
        email: editForm.email.trim() || undefined,
        phone: editForm.phone.trim() || undefined,
        user_group: activeGroupId ?? undefined,
      })

      if (activeTab === 'business' && editForm.primaryAddressId) {
        await orgUsersApi.updateAddress(editForm.primaryAddressId, {
          opening_time: editForm.openingTime.trim() || undefined,
          closing_time: editForm.closingTime.trim() || undefined,
          saturday_closed: editForm.saturdayClosed,
          sunday_closed: editForm.sundayClosed,
        })
      }

      return updatedCustomer
    },
    onSuccess: (updatedCustomer) => {
      toast.success('Customer details updated')
      closeEditCustomerDialog()
      queryClient.invalidateQueries({ queryKey: ['customer-directory'] })
      queryClient.invalidateQueries({ queryKey: ['customer-detail', String(updatedCustomer.id)] })
      if (updatedCustomer.user_id) {
        queryClient.invalidateQueries({ queryKey: ['customer-detail', String(updatedCustomer.user_id)] })
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const createCustomerMutation = useMutation({
    mutationFn: async () => {
      if (!activeGroupId) {
        throw new Error('No customer group is configured for this branch.')
      }

      return orgUsersApi.createClient({
        title: editForm.title.trim() || undefined,
        fname: editForm.fname.trim(),
        lname: editForm.lname.trim() || undefined,
        email: editForm.email.trim() || undefined,
        phone: editForm.phone.trim() || undefined,
        user_group: activeGroupId,
      }, {
        serviceInstanceId,
      })
    },
    onSuccess: (createdCustomer) => {
      toast.success('Customer created')
      closeAddCustomerDialog()
      queryClient.invalidateQueries({ queryKey: ['customer-directory'] })
      queryClient.invalidateQueries({ queryKey: ['customer-detail', String(createdCustomer.id)] })
      if (createdCustomer.user_id) {
        queryClient.invalidateQueries({ queryKey: ['customer-detail', String(createdCustomer.user_id)] })
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const isCustomerFormDialogOpen = Boolean(editingCustomer) || isAddCustomerDialogOpen
  const isCreateCustomerMode = isAddCustomerDialogOpen && !editingCustomer
  const isSavingCustomer = updateCustomerMutation.isPending || createCustomerMutation.isPending

  const deleteCustomerMutation = useMutation({
    mutationFn: async (customer: OrgUser) => {
      const customerId = String(customer.id ?? customer.user_id)
      return orgUsersApi.deleteClient(customerId)
    },
    onSuccess: () => {
      toast.success('Customer deleted')
      setDeletingCustomer(null)
      queryClient.invalidateQueries({ queryKey: ['customer-directory'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ customer, password }: { customer: OrgUser; password: string }) => {
      const userId = customer.user_id ?? customer.id
      if (!userId) {
        throw new Error('Customer user id is unavailable.')
      }
      return orgUserAdminApi.forcePasswordReset(userId, serviceInstanceId, password)
    },
    onSuccess: () => {
      toast.success('Password reset successfully. The customer must change it at next sign in.')
      setResetPasswordCustomer(null)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const countColumnLabel = context === 'bookings' ? 'Bookings' : 'Jobs'
  const customers = customersQuery.data?.results ?? []
  const totalCount = customersQuery.data?.total ?? 0
  const unpaidTotals = customersQuery.data?.unpaid_totals
  const pageUnpaidTotal = useMemo(
    () => customers.reduce((sum, customer) => {
      const amount = Number(customer.total_unpaid_booking_amount ?? 0)
      return Number.isFinite(amount) ? sum + amount : sum
    }, 0),
    [customers],
  )
  const pageCustomersWithUnpaid = useMemo(
    () => customers.filter((customer) => Number(customer.total_unpaid_booking_amount ?? 0) > 0).length,
    [customers],
  )
  const unpaidCustomerCount = Number(unpaidTotals?.customers_with_unpaid ?? pageCustomersWithUnpaid)
  const unpaidCustomerAmount = Number(unpaidTotals?.total_unpaid_booking_amount ?? pageUnpaidTotal)
  const isLoading = groupsQuery.isLoading || customersQuery.isLoading

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="retail">Retail Customers</TabsTrigger>
          <TabsTrigger value="business">Business Customers</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4 space-y-4">
          <Card>
            <CardHeader className="flex flex-col gap-4 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4" />
                {activeTab === 'business' ? 'Business Customers' : 'Retail Customers'}
              </CardTitle>
              <div className="flex w-full flex-col gap-2 sm:max-w-md sm:flex-row sm:items-center">
                <div className="relative w-full sm:flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value)
                      setPage(1)
                    }}
                    placeholder="Search customers..."
                    className="pl-9"
                  />
                </div>
                {activeGroupId ? (
                  <Button type="button" size="sm" className="shrink-0" onClick={openAddCustomerDialog}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add customer
                  </Button>
                ) : null}
              </div>
            </CardHeader>
            <div className="border-b border-border/60 px-6 py-3">
              <div className="flex flex-col gap-3 rounded-xl border border-rose-200/80 bg-rose-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-sm text-rose-800">
                  <CreditCard className="h-4 w-4 shrink-0" />
                  <span className="font-medium">Total unpaid (filtered customers)</span>
                </div>
                <div className="sm:text-right">
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-rose-700" />
                  ) : (
                    <>
                      <p className="text-lg font-semibold text-rose-900">
                        {Number.isFinite(unpaidCustomerAmount) && unpaidCustomerAmount > 0
                          ? formatCurrency(unpaidCustomerAmount)
                          : formatCurrency(0)}
                      </p>
                      <p className="text-xs text-rose-700">
                        {unpaidCustomerCount} customer{unpaidCustomerCount === 1 ? '' : 's'} with unpaid bookings
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
            <CardContent className="p-0">
              {!activeGroupId && !groupsQuery.isLoading ? (
                <div className="px-6 py-10 text-sm text-muted-foreground">
                  No {activeTab === 'business' ? 'business' : 'retail'} customer group is configured for this branch.
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center gap-2 px-6 py-10 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading customers...
                </div>
              ) : customers.length === 0 ? (
                <div className="px-6 py-10 text-sm text-muted-foreground">
                  No customers found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className={commerceTableClassName}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last booking</TableHead>
                        <TableHead>Last job</TableHead>
                        <TableHead>Unpaid</TableHead>
                        <TableHead>Credit</TableHead>
                        <TableHead>{countColumnLabel}</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customers.map((customer) => {
                        const userId = customer.user_id ?? customer.id
                        const openCount = context === 'bookings'
                          ? customer.open_booking_count ?? 0
                          : customer.open_job_count ?? 0
                        const completedCount = context === 'bookings'
                          ? customer.completed_booking_count ?? 0
                          : customer.completed_job_count ?? 0
                        const detailId = customer.id ?? userId

                        return (
                          <TableRow key={String(userId)}>
                            <TableCell className="font-medium">
                              <Button variant="link" className="h-auto p-0 text-left font-medium" asChild>
                                <Link to={buildCustomerDetailLink(detailId)}>
                                  {getCustomerDisplayName(customer)}
                                </Link>
                              </Button>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{customer.email || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getStatusBadgeClassName(customer.status)}>
                                {customer.status || 'Unknown'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <CompactTableDate timeZone={customerTimeZone ?? undefined} value={customer.last_booking_at} />
                            </TableCell>
                            <TableCell>
                              <CompactTableDate timeZone={customerTimeZone ?? undefined} value={customer.last_job_at} />
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatUnpaidAmount(customer.total_unpaid_booking_amount)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {context === 'bookings'
                                ? formatUnpaidAmount(customer.credit_outstanding_balance)
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="text-muted-foreground">Open</span>
                                <Button variant="link" className="h-auto p-0" asChild>
                                  <Link to={context === 'bookings' ? buildBookingsLink(userId, 'open') : buildJobsLink(userId, 'open')}>
                                    {openCount}
                                  </Link>
                                </Button>
                                <span className="text-muted-foreground">Completed</span>
                                <Button variant="link" className="h-auto p-0" asChild>
                                  <Link to={context === 'bookings' ? buildBookingsLink(userId, 'completed') : buildJobsLink(userId, 'completed')}>
                                    {completedCount}
                                  </Link>
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    aria-label={`Actions for ${getCustomerDisplayName(customer)}`}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-40">
                                  <DropdownMenuItem asChild>
                                    <Link to={buildCustomerDetailLink(detailId)}>
                                      <Eye className="mr-2 h-4 w-4" />
                                      View
                                    </Link>
                                  </DropdownMenuItem>
                                  {context === 'bookings' ? (
                                    <DropdownMenuItem asChild>
                                      <Link to={buildCreditLink(userId)}>
                                        <CircleDollarSign className="mr-2 h-4 w-4" />
                                        Credit
                                      </Link>
                                    </DropdownMenuItem>
                                  ) : null}
                                  <DropdownMenuItem onClick={() => openEditCustomerDialog(customer)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit
                                  </DropdownMenuItem>
                                  {canForcePasswordResetUsers ? (
                                    <DropdownMenuItem
                                      onClick={() => setResetPasswordCustomer(customer)}
                                      disabled={
                                        resetPasswordMutation.isPending
                                        || String(customer.user_id ?? customer.id) === String(currentUserId ?? '')
                                      }
                                    >
                                      <KeyRound className="mr-2 h-4 w-4" />
                                      Reset password
                                    </DropdownMenuItem>
                                  ) : null}
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => setDeletingCustomer(customer)}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                  <div className="border-t border-border/70 p-4">
                    <DataTablePagination
                      page={page}
                      pageSize={pageSize}
                      totalCount={totalCount}
                      onPageChange={setPage}
                      onPageSizeChange={(value) => {
                        setPageSize(value)
                        setPage(1)
                      }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ResetPasswordDialog
        open={Boolean(resetPasswordCustomer)}
        onOpenChange={(open) => {
          if (!open && !resetPasswordMutation.isPending) {
            setResetPasswordCustomer(null)
          }
        }}
        subjectName={resetPasswordCustomer ? getCustomerDisplayName(resetPasswordCustomer) : ''}
        entityLabel="customer"
        isPending={resetPasswordMutation.isPending}
        onConfirm={(password) => {
          if (resetPasswordCustomer) {
            resetPasswordMutation.mutate({ customer: resetPasswordCustomer, password })
          }
        }}
      />

      <Dialog
        open={isCustomerFormDialogOpen}
        onOpenChange={(open) => {
          if (!open && !isSavingCustomer) {
            if (isCreateCustomerMode) {
              closeAddCustomerDialog()
            } else {
              closeEditCustomerDialog()
            }
          }
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{isCreateCustomerMode ? 'Add customer' : 'Edit customer details'}</DialogTitle>
            <DialogDescription>
              {isCreateCustomerMode
                ? `Create a new ${activeTab === 'business' ? 'business' : 'retail'} customer for this branch.`
                : 'Update the customer\'s contact details for this branch.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="customer-title">Title</Label>
              <Input
                id="customer-title"
                value={editForm.title}
                onChange={(event) => setEditForm((current) => ({ ...current, title: event.target.value }))}
                disabled={isSavingCustomer}
                placeholder="Mr, Ms, Dr..."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="customer-first-name">First name</Label>
              <Input
                id="customer-first-name"
                value={editForm.fname}
                onChange={(event) => setEditForm((current) => ({ ...current, fname: event.target.value }))}
                disabled={isSavingCustomer}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="customer-last-name">Last name</Label>
              <Input
                id="customer-last-name"
                value={editForm.lname}
                onChange={(event) => setEditForm((current) => ({ ...current, lname: event.target.value }))}
                disabled={isSavingCustomer}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="customer-email">Email</Label>
              <Input
                id="customer-email"
                type="email"
                value={editForm.email}
                onChange={(event) => setEditForm((current) => ({ ...current, email: event.target.value }))}
                disabled={isSavingCustomer}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="customer-phone">Phone</Label>
              <Input
                id="customer-phone"
                value={editForm.phone}
                onChange={(event) => setEditForm((current) => ({ ...current, phone: event.target.value }))}
                disabled={isSavingCustomer}
              />
              <p className="text-xs text-muted-foreground">Either email or phone is required.</p>
            </div>

            {activeTab === 'business' && !isCreateCustomerMode ? (
              <div className="space-y-3 rounded-lg border border-border/70 bg-muted/15 p-4 sm:col-span-2">
                <div>
                  <p className="text-sm font-medium text-foreground">Business hours</p>
                  <p className="text-xs text-muted-foreground">
                    {editForm.primaryAddressId
                      ? 'Set opening hours and weekend availability for the primary business address.'
                      : 'Add a business address on the customer detail page to configure hours here.'}
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="customer-opening-time">Opening time</Label>
                    <Input
                      id="customer-opening-time"
                      type="time"
                      value={editForm.openingTime}
                      onChange={(event) => setEditForm((current) => ({ ...current, openingTime: event.target.value }))}
                      disabled={isSavingCustomer || !editForm.primaryAddressId}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="customer-closing-time">Closing time</Label>
                    <Input
                      id="customer-closing-time"
                      type="time"
                      value={editForm.closingTime}
                      onChange={(event) => setEditForm((current) => ({ ...current, closingTime: event.target.value }))}
                      disabled={isSavingCustomer || !editForm.primaryAddressId}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="customer-saturday-closed"
                      checked={editForm.saturdayClosed}
                      onCheckedChange={(checked) => setEditForm((current) => ({ ...current, saturdayClosed: checked === true }))}
                      disabled={isSavingCustomer || !editForm.primaryAddressId}
                    />
                    <Label htmlFor="customer-saturday-closed" className="font-normal">Closed on Saturday</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="customer-sunday-closed"
                      checked={editForm.sundayClosed}
                      onCheckedChange={(checked) => setEditForm((current) => ({ ...current, sundayClosed: checked === true }))}
                      disabled={isSavingCustomer || !editForm.primaryAddressId}
                    />
                    <Label htmlFor="customer-sunday-closed" className="font-normal">Closed on Sunday</Label>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={isCreateCustomerMode ? closeAddCustomerDialog : closeEditCustomerDialog}
              disabled={isSavingCustomer}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                isSavingCustomer
                || !editForm.fname.trim()
                || (!editForm.email.trim() && !editForm.phone.trim())
              }
              onClick={() => {
                if (isCreateCustomerMode) {
                  createCustomerMutation.mutate()
                  return
                }
                updateCustomerMutation.mutate()
              }}
            >
              {isSavingCustomer
                ? (isCreateCustomerMode ? 'Creating...' : 'Saving...')
                : (isCreateCustomerMode ? 'Create customer' : 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={Boolean(deletingCustomer)}
        onOpenChange={(open) => {
          if (!open && !deleteCustomerMutation.isPending) {
            setDeletingCustomer(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingCustomer
                ? `Are you sure you want to delete "${getCustomerDisplayName(deletingCustomer)}"? This action cannot be undone.`
                : 'Are you sure you want to delete this customer?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteCustomerMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCustomerMutation.isPending || !deletingCustomer}
              onClick={(event) => {
                event.preventDefault()
                if (deletingCustomer) {
                  deleteCustomerMutation.mutate(deletingCustomer)
                }
              }}
            >
              {deleteCustomerMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
